import { DASHBOARD_MAX_WINDOW_DAYS, readableDate } from '@pams/shared';

/**
 * Every word this screen says, in one place — so prose carries a test and cannot be deleted
 * in a tidy-up without something going red. `EmptyState`'s stated reason.
 */
export const DASHBOARD_COPY = {
  title: 'Dashboard',
  /*
   * ⚠️ `todayHint`, `scope` AND `wholeCommissionerate` WERE HERE AND ARE GONE, because the
   * header no longer carries a subtitle. §9: a string with no reader is dead code, and this
   * file's own header says the point of it is that prose carries a test — a key nothing
   * renders carries nothing. The default range is still stated by the pickers' placeholders
   * below; the scope sentence is simply not said any more.
   */
  /*
   * ⚠️ [UX1 §7] THE COLON IS IN THE COPY, NOT IN THE PRIMITIVE.
   *
   * `FilterField.label` is a plain string with thirteen other consumers passing bare words on
   * filter ROWS, where a trailing colon would be wrong — so appending one inside the component
   * when `layout === 'inline'` would put a rendering decision in a primitive and make the
   * string in this file no longer the string on screen. This file's own premise is that every
   * word the screen says lives here.
   */
  filters: { from: 'From:', to: 'To:' },
  fromPlaceholder: 'Today',
  /* ⚠️ [C1c §5b] The SERVER's default, stated as a placeholder rather than written into the URL. */
  toPlaceholder: 'In 3 days',
  loading: 'Loading your dashboard',
  loadingTile: (title: string) => `Loading ${title.toLowerCase()}`,
  tileRetry: 'Try again',
  emptyTitle: 'Nothing in this range',
  emptyDescription: `Pick another date. The dashboard covers up to ${String(DASHBOARD_MAX_WINDOW_DAYS)} days at a time.`,
  /** ⚠️ Said out loud, because a tile that is off is not a tile that is broken. */
  offCount: (count: number) => `${String(count)} more tile${count === 1 ? '' : 's'} available`,
  rowsParent: 'Dashboard',
  rowsEmpty: 'Nothing to show for these dates.',
  truncated: (shown: number, total: number) => `Showing ${String(shown)} of ${String(total)}.`,
} as const;

/** The three headline attendance buckets, and the two fulfilment bands, named once. */
export const TILE_COPY = {
  /** ⚠️ [C1e §1a] A real answer of "nothing on today", never a blank card. */
  noDutiesToday: 'No duty in range within your scope.',
  /**
   * ⚠️ [ASSOC-TILE] A REAL ANSWER, AND IT NAMES THE FIELD THE TILE FILTERS ON.
   *
   * "Nothing here" on this card would be read as "we are attached to nothing, ever" rather
   * than "nothing in these dates", which is the one thing an empty state on a range-obeying
   * tile must not say.
   */
  noAssociatedOrders: 'No order in range names your unit as an associated PS or unit.',
  /**
   * ⚠️ [ASSOC-TILE] THE OPERATIONAL SPAN. BOTH ENDS ARE NULLABLE ON THE WIRE AND NEITHER CAN
   * CURRENTLY ARRIVE NULL — THE BRANCH IS A FLOOR, NOT A CASE.
   *
   * `first_duty_date`/`last_duty_date` are `default: null` and DERIVED by `syncCounts`, so an
   * unscheduled order has neither — but `resolveSpine` matches both with `$gte`/`$lte` and no
   * null arm, and Mongo does not match `$gte` against `null`, so such an order never reaches
   * this tile. ([C3c §8a] added that null arm to `listArrangements` and explains why; the spine
   * has none.) The branch stays because a renderer must never print `null` into a cell, and
   * because the day the spine gains a null arm this reads correctly with no edit.
   *
   * ⚠️ ONE DATE WHEN THE TWO AGREE. A single-day order rendering "16 Aug – 16 Aug" is the same
   * date read twice, which is noise on a row that has three other things on it.
   */
  dutySpan: (first: string | null, last: string | null): string => {
    if (first === null || last === null) return 'No duty scheduled';
    const from = readableDate(first) ?? first;
    const to = readableDate(last) ?? last;
    return from === to ? from : `${from} – ${to}`;
  },
  attendance: { reached: 'Reached', notReached: 'Not reached', absent: 'Absent' },
  fulfilment: {
    named: 'Named',
    sourced: 'Sourced, not named',
    unsourced: 'Still to source',
    /* ⚠️ [C2b §6a] The row's caption — `12 named` reads beside `of 40`. */
    ofRequired: (required: number): string => `of ${required.toLocaleString('en-IN')}`,
    /* ⚠️ A SILENT TRUNCATION READS AS "that is all there is". It never is. */
    more: (count: number): string => `${count.toLocaleString('en-IN')} more not shown`,
  },
  unnamedSome: 'still to name',
  slots: 'slots',
  duties: 'duties',
  swaps: 'swaps',
  noRows: 'Nothing here',
  /** ⚠️ [C1g §1e] A real answer, never a blank card — `noDutiesToday`'s rule. */
  nothingToFill: 'Every order in range is named.',
  dutyCount: (count: number) => `${String(count)} dut${count === 1 ? 'y' : 'ies'}`,
} as const;
